import env from 'enviro';
import { getFullUrl } from 'hubspot-url-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { AuthStepItemView, getParamDefaultsFromSteps, validateAuthStep, buildApiPayload } from './UniversalAuthSteps';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { ConnectedAccount } from './UniversalAuthAccount';
import { View } from 'foundations-components/transitional/components/View';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { Flex } from 'foundations-components/components/Flex';
import { Section } from 'foundations-components/transitional/components/Section';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Button } from 'foundations-components/transitional/components/Button';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Image } from 'foundations-components/transitional/components/Image';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { HR } from 'foundations-components/transitional/components/HR';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Text } from 'foundations-components/components/Text';
import { fetchSessionInfo } from './Api';
import { openOAuthWindow } from './OAuth';
import { useHttpClient } from './HttpClientContext';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ModalOrInlineLayout({
  isModal,
  header,
  body,
  footer
}) {
  if (isModal) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [header && /*#__PURE__*/_jsx(DialogHeader, {
        children: header
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: body
      }), footer && /*#__PURE__*/_jsx(DialogFooter, {
        children: footer
      })]
    });
  } else {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [header || null, body, footer || null]
    });
  }
}
export class DuplicateDataSyncAccountError extends Error {
  constructor(portalId, appId, account) {
    super();
    this.name = 'DuplicateDataSyncAccountError';
    this.portalId = portalId;
    this.appId = appId;
    this.account = account;
  }
  toAuthError() {
    return {
      type: 'DuplicateDataSyncAccount',
      portalId: this.portalId,
      appId: this.appId,
      account: this.account
    };
  }
}
export class MissingPermissions extends Error {
  constructor() {
    super();
    this.type = 'MissingPermissions';
  }
  toAuthError() {
    return {
      type: 'MissingPermissions'
    };
  }
}
export class AuthenticateError extends Error {
  constructor(message, upstreamErrorMessage) {
    super(message);
    this.name = 'AuthenticateError';
    this.type = 'AuthenticateError';
    this.message = message || '';
    this.upstreamErrorMessage = upstreamErrorMessage;
  }
  toAuthError() {
    return {
      type: 'AuthenticateError',
      message: this.message,
      upstreamErrorMessage: this.upstreamErrorMessage
    };
  }
}

// rewrites urls to use a locally running BE service.
export function parseLocalApiOverride(url) {
  let LOCAL_API_OVERRIDES = [];
  try {
    const overrides = localStorage.getItem('LOCAL_API_OVERRIDES');
    if (overrides) {
      LOCAL_API_OVERRIDES = JSON.parse(overrides);
      if (!Array.isArray(LOCAL_API_OVERRIDES)) {
        console.error('LOCAL_API_OVERRIDES local storage key must be a stringified array');
        return url;
      }
    }
  } catch (__error) {
    return url;
  }
  if (LOCAL_API_OVERRIDES.some(overrideString => url.includes(overrideString))) {
    return url.replace(/https:\/\/(app|api)/, 'https://local').replace('/api/', '/');
  }
  return url;
}
const oAuthRedirectOrigin = `https://oauth-redirect.hubspot${env.isQa() ? 'qa' : ''}.com`;
function isOAuth(type) {
  return type === 'OAuth' || type === 'OAuthWithPolling';
}
function isOAuthWithoutPolling(type) {
  return type === 'OAuth';
}
function isOAuthWithPolling(type) {
  return type === 'OAuthWithPolling';
}

// In a polling flow the popup closing isn't an observable signal, so we show the
// spinner for this long and then keep polling silently in the background. Timed
// against the wall clock rather than a poll count, which drifts from real time
// with network latency and timer throttling.
const SPINNER_VISIBLE_MS = 5000;
function FinishButton({
  sessionId,
  type,
  portalId,
  appName,
  disabled,
  steps,
  params,
  oAuthScope,
  mcp,
  appId,
  onSuccess,
  onFailure,
  getAuthorizeUrl,
  finishAuthFlow
}) {
  const http = useHttpClient();
  const [status, setStatus] = useState('idle');
  const [spinnerExpired, setSpinnerExpired] = useState(false);
  const oauthStateRef = useRef('');
  const oauthWindowRef = useRef(undefined);
  useEffect(() => {
    if (isOAuth(type) && status === 'finishing') {
      let pollSessionInfoTimeout;
      let spinnerTimeout;
      const sessionTimeout = setTimeout(() => {
        clearTimeout(pollSessionInfoTimeout);
        setStatus('cancelled');
        onFailure({
          type: 'OAuthWindowTimeout'
        });
        if (oauthWindowRef.current) {
          oauthWindowRef.current.close();
        }
      }, 300000);

      // If polling via the UAB, we don't need to poll the oauth window
      // because the CSP from the third party will break the connection
      // between the oauth window and the parent window.
      const heartbeat = isOAuthWithoutPolling(type) ? setInterval(() => {
        if (!oauthWindowRef.current || oauthWindowRef.current.closed) {
          setStatus('cancelled');
          onFailure({
            type: 'OAuthWindowClosed'
          });
        }
      }, 1000) : undefined;
      const handleAuthResponse = (oauthStatus, oauthParams) => {
        // CSRF protection: https://datatracker.ietf.org/doc/html/rfc6749#section-10.12
        // Verify the validity of the request by matching the received state with the user-agent's authenticated state.
        // We have 3 cases:
        //  1. There’s a state parameter and it matches the user-agent’s authenticated state
        //  2. The state does not match, the request is invalid and should be rejected
        //  3. There is no state, the request is invalid and should be rejected
        if (oauthParams.has('state')) {
          // Case 1. There’s a state parameter and it matches the user-agent’s authenticated state
          if (oauthStateRef.current === oauthParams.get('state')) {
            // Handle authorization error redirect information received indirectly from the popup
            // and ensure all expected redirect search parameters are present
            if (oauthStatus === 'AUTH_ERROR' && oauthParams.has('message')) {
              setStatus('failed');
              onFailure({
                type: 'AuthorizeError',
                message: oauthParams.get('message') || undefined
              });
            } else if (oauthStatus === 'AUTH_RESPONSE') {
              const payload = buildApiPayload(sessionId, steps, params, oauthParams, oauthStateRef.current, oAuthScope, mcp, appId);
              finishAuthFlow(payload).then(finishPayload => {
                setStatus('idle');
                onSuccess(finishPayload);
              }).catch(err => {
                setStatus('failed');
                if (err instanceof AuthenticateError || err instanceof DuplicateDataSyncAccountError || err instanceof MissingPermissions) {
                  onFailure(err.toAuthError());
                } else if (err.responseJSON && err.responseJSON.message) {
                  onFailure({
                    type: 'AuthenticateError',
                    message: err.responseJSON.message
                  });
                } else {
                  onFailure({
                    type: 'AuthenticateError',
                    message: err.message
                  });
                }
              });
            } else {
              // neither AUTH_ERROR nor AUTH_RESPONSE type
              onFailure({
                type: 'UnknownOAuthProblem'
              });
              setStatus('failed');
            }
          } else {
            // Case 2: The state does not match, the request is invalid and should be rejected
            onFailure({
              type: 'UnknownOAuthProblem'
            });
            setStatus('failed');
          }
        } else {
          // Case 3: There is no state, the request is invalid and should be rejected
          onFailure({
            type: 'UnknownOAuthProblem'
          });
          setStatus('failed');
        }

        // TODO: we should send case 2 & 3 to the UnifiedAuth BE in the future so we can track them and present a nice error message.

        clearTimeout(pollSessionInfoTimeout);
        clearTimeout(sessionTimeout);
        clearTimeout(spinnerTimeout);
        clearInterval(heartbeat);
        oauthStateRef.current = '';
      };
      const pollSessionInfo = () => {
        fetchSessionInfo(http, portalId, sessionId).then(response => {
          if (status === 'finishing' && response.status === 'PENDING') {
            pollSessionInfoTimeout = setTimeout(pollSessionInfo, 1000);
          } else {
            handleAuthResponse(response.status, response.params);
          }
        }).catch(() => {
          // If the session info request fails, we can't do anything
          // so we call the handler with an error
          handleAuthResponse('AUTH_ERROR', new Map());
        });
      };
      const messageHandler = event => {
        const oauthWindow = oauthWindowRef.current;
        if (event.data && oauthWindow && event.source === oauthWindow) {
          if ((event.origin === oAuthRedirectOrigin || event.origin === getFullUrl('app')) && event.data === 'ping') {
            oauthWindow.postMessage('pong', event.origin);
          } else if (event.origin === oAuthRedirectOrigin) {
            const oAuthRedirectSearchParams = new URLSearchParams(event.data.queryString);
            handleAuthResponse(event.data.type, oAuthRedirectSearchParams);
            oauthWindow.close();
          }
        }
      };
      if (isOAuthWithPolling(type)) {
        spinnerTimeout = window.setTimeout(() => {
          setSpinnerExpired(true);
        }, SPINNER_VISIBLE_MS);
        pollSessionInfo();
      }

      // Set up listener for messages from the oauth window
      // even with polling, messages can still come in when
      // the authorize params are invalid
      window.addEventListener('message', messageHandler);
      return () => {
        clearTimeout(sessionTimeout);
        clearInterval(heartbeat);
        clearTimeout(pollSessionInfoTimeout);
        clearTimeout(spinnerTimeout);
        window.removeEventListener('message', messageHandler);
      };
    }
    return undefined;
    // eslint-disable-next-line
  }, [status]);
  return /*#__PURE__*/_jsx(LoadingButton, {
    use: "primary",
    disabled: disabled
    // In a polling flow the popup closing isn't an observable signal, so we
    // only show the spinner for SPINNER_VISIBLE_MS and then keep polling in
    // the background. Non-polling flows never arm the spinner timer, so the
    // spinner stays visible for the entire duration of the auth flow.
    ,
    loading: !spinnerExpired && status === 'finishing',
    failed:
    // Why also finishing? The `failed` prop is used to show a checkmark
    // if not true. If true, no checkmark is shown, which is what we want
    // in the case of a polling flow because we don't know if the auth flow
    // was successful. For non-polling flows, this is fine as well, because
    // the loading spinner is shown for the entire duration of the auth flow
    status === 'failed' || status === 'cancelled' || status === 'finishing',
    onClick: () => {
      // Reset the spinner timer for the next auth flow
      setSpinnerExpired(false);
      setStatus('finishing');
      if (isOAuth(type)) {
        const oAuthWindow = openOAuthWindow(appName);
        if (!oAuthWindow) {
          // The window wasn't allowed to open
          // This is likely caused by built-in popup blockers or browser settings.
          setStatus('failed');
          onFailure({
            type: 'OAuthWindowBlocked'
          });
          return;
        }
        oauthStateRef.current = sessionId;
        const {
          inputParams
        } = buildApiPayload(sessionId, steps, params, undefined, undefined, undefined, undefined, appId);
        const oAuthQuery = Object.assign({
          sessionId
        }, oAuthScope !== undefined ? {
          scope: oAuthScope
        } : {}, {
          state: sessionId,
          portalId
        }, mcp !== null && mcp !== void 0 && mcp.clientId ? {
          clientId: mcp.clientId
        } : {}, mcp !== null && mcp !== void 0 && mcp.serverUrl ? {
          mcpServerUrl: mcp.serverUrl
        } : {}, inputParams);
        getAuthorizeUrl(oAuthQuery).then(({
          authorizeUrl
        }) => {
          if (oAuthWindow !== null) {
            // window.open doesn't go through the hub-http stack, so we need to manually parse local api overrides
            oAuthWindow.location = parseLocalApiOverride(authorizeUrl);
            oauthWindowRef.current = oAuthWindow;
          }
        }).catch(err => {
          var _error$responseJSON;
          const error = typeof err === 'object' && err !== null ? err : undefined;
          oAuthWindow === null || oAuthWindow === void 0 || oAuthWindow.close();
          setStatus('failed');
          onFailure({
            type: 'AuthorizeError',
            message: (error === null || error === void 0 || (_error$responseJSON = error.responseJSON) === null || _error$responseJSON === void 0 ? void 0 : _error$responseJSON.message) || (error === null || error === void 0 ? void 0 : error.message)
          });
        });
      } else {
        const payload = buildApiPayload(sessionId, steps, params, undefined, undefined, undefined, undefined, appId);
        finishAuthFlow(payload).then(finishPayload => {
          setStatus('idle');
          onSuccess(finishPayload);
        }).catch(err => {
          setStatus('failed');
          if (err instanceof AuthenticateError || err instanceof DuplicateDataSyncAccountError) {
            onFailure(err.toAuthError());
          } else if (err.responseJSON && err.responseJSON.message) {
            onFailure({
              type: 'AuthenticateError',
              message: err.responseJSON.message
            });
          } else {
            onFailure({
              type: 'AuthenticateError',
              message: err.message
            });
          }
        });
      }
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalAuth.auth.connectTo",
      options: {
        appName
      }
    })
  });
}
function AuthConfigLoadingState({
  isModal,
  onCancel
}) {
  return /*#__PURE__*/_jsx(ModalOrInlineLayout, {
    isModal: isModal,
    header: isModal ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.modal.loadingDialogHeader"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      })]
    }) : undefined,
    body: /*#__PURE__*/_jsx(View, {
      minBlockSize: "300px",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered"
      })
    })
  });
}
function AuthConfigFetchFailure({
  isModal,
  onClose
}) {
  return /*#__PURE__*/_jsx(ModalOrInlineLayout, {
    isModal: isModal,
    header: isModal ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.modal.loadingDialogHeader"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }) : undefined,
    body: /*#__PURE__*/_jsx(View, {
      minBlockSize: "300px",
      children: /*#__PURE__*/_jsx(Alert, {
        type: "danger",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.errors.configuration.title"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.errors.configuration.body"
        })
      })
    })
  });
}
// TODO: I found this code in the old error alert. I'm not sure if it's still relevant.

// `errorMessage` comes from the connector and will be HTML-escaped
// hence why we're using `FormattedHTMLMessage` which handles both the cleaning
// and parsing of the HTML entities
function AuthErrorAlert({
  appName,
  error
}) {
  if (error.type === 'AuthorizeError') {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAuth.errors.authorize.titleText",
        options: {
          appName
        }
      }),
      children: error.message || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAuth.errors.authorize.default"
      })
    });
  } else if (error.type === 'AuthenticateError') {
    return /*#__PURE__*/_jsxs(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAuth.errors.authenticate.titleText",
        options: {
          appName
        }
      }),
      children: [error.message || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAuth.errors.authenticate.default"
      }), error.upstreamErrorMessage && /*#__PURE__*/_jsx(AccordionItem, {
        marginBlockStart: "400",
        title: /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.errors.accountCreation.details"
          })
        }),
        children: /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "universalAuth.errors.accountCreation.htmlMessage",
            options: {
              message: error.upstreamErrorMessage
            }
          })
        })
      })]
    });
  } else if (error.type === 'OAuthWindowBlocked') {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.oauthWindowBlocked.titleText",
        options: {
          appName
        }
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.oauthWindowBlocked.instructions",
        options: {
          appName
        }
      })
    });
  } else if (error.type === 'OAuthWindowClosed') {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.oauthWindowClosed.titleText",
        options: {
          appName
        }
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.oauthWindowClosed.instructions"
      })
    });
  } else if (error.type === 'OAuthWindowTimeout') {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.oauthWindowTimedOut.titleText",
        options: {
          appName
        }
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.oauthWindowTimedOut.instructions"
      })
    });
  } else if (error.type === 'UnknownOAuthProblem') {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAuth.errors.authenticate.titleText",
        options: {
          appName
        }
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.pleaseTryAgain"
      })
    });
  } else if (error.type === 'MissingPermissions') {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.missingPermissions.titleText",
        options: {
          appName
        }
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuth.errors.missingPermissions.body"
      })
    });
  }
  return null;
}
// We have the following cases:
// 1. OAuth with intro screen and Input with intro screen
//  - show intro screen for OAuth
//  - show intro screen for Input
// 2. OAuth without intro screen and Input without intro screen
//  - show default intro for OAuth
//  - no intro screen, render first step of Input auth flow
// 3. OAuth without intro screen and Input with intro screen
//  - show intro screen for Oauth
//  - no intro screen, render first step of Input auth flow
function IntroStep({
  type,
  appName,
  introScreen
}) {
  // To support case 3, callers can dynamically render an intro screen.
  // So we render it here to see if we actually have one.
  const renderedIntro = introScreen ? introScreen({
    type,
    appName
  }) : null;
  if (renderedIntro) {
    return renderedIntro;
  } else if (type === 'OAuth' || type === 'OAuthWithPolling') {
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      "data-test-id": "oauth-intro",
      message: "universalAuth.auth.connectDefaultOAuthIntro",
      options: {
        appName
      }
    });
  }
  return null;
}
export function DuplicateAccountStep({
  isModal,
  portalId,
  appId,
  appName,
  account,
  onCancel,
  onReset
}) {
  return /*#__PURE__*/_jsx(ModalOrInlineLayout, {
    isModal: isModal,
    header: isModal ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexGrow: 1,
          gap: "200",
          children: [/*#__PURE__*/_jsx(WarningIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.modal.duplicateAccountHeader"
          })]
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      })]
    }) :
    // let's not show the modal header for inline
    undefined,
    body: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ConnectedAccount, {
        portalId: portalId,
        appId: appId,
        account: account,
        error: true
      }), /*#__PURE__*/_jsx(Section, {
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.auth.tryDifferentAccount",
          options: {
            appName
          }
        })
      })]
    }),
    footer: /*#__PURE__*/_jsxs(ButtonWrapper, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: onReset,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.auth.connectDifferentAccount"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.auth.cancel"
        })
      })]
    })
  });
}
function LowLevelAuthFlowView({
  isModal,
  isReauth,
  portalId,
  authState,
  authConfig: {
    appName,
    appIcon,
    oAuthScope,
    mcp,
    authFlow,
    appId,
    getAuthorizeUrl,
    finishAuthFlow
  },
  onComplete,
  onCancel,
  introScreen,
  successScreen
}) {
  var _authFlow$steps;
  // Controls whether multiple steps are rendered on one screen or one screen per step. Defaults to false.
  const {
    inline = false
  } = authFlow;
  const steps = (_authFlow$steps = authFlow.steps) !== null && _authFlow$steps !== void 0 ? _authFlow$steps : [];

  // -1 means intro screen
  // 0 .. length - 1 means auth steps
  const [stepIndex, setStepIndex] = useState(() => {
    if (inline) {
      return -1;
    }

    // See cases on `<IntroStep />` for more context.
    // Callers can supply an intro screen for OAuth, but not for Input auth flows.
    // So we render it here to see if we start with the intro screen or the first step.
    const renderedIntro = introScreen ? introScreen({
      type: authFlow.type,
      appName
    }) : null;
    return renderedIntro || steps.length === 0 && isOAuth(authFlow.type) ? -1 /* intro screen */ : 0 /* first step */;
  });

  // Compute and cache the step index of the first screen forever
  // We want to render the eu hublet legal message only on the first screen
  const firstScreenIndex = useMemo(() => stepIndex, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [params, setParams] = useState(() => getParamDefaultsFromSteps(steps));
  const currentStep = !inline && stepIndex > -1 && stepIndex < steps.length ? steps[stepIndex] : undefined;
  const isValidStep = currentStep ? validateAuthStep(currentStep, params) : true;
  const allStepsValid = steps.every(step => validateAuthStep(step, params));
  const [authError, setAuthError] = useState();
  if (authError && authError.type === 'DuplicateDataSyncAccount') {
    return /*#__PURE__*/_jsx(DuplicateAccountStep, {
      isModal: isModal,
      portalId: authError.portalId,
      appId: authError.appId,
      appName: appName,
      account: authError.account,
      onCancel: onCancel,
      onReset: () => {
        setStepIndex(() => {
          const renderedIntro = introScreen ? introScreen({
            type: authFlow.type,
            appName
          }) : null;
          return renderedIntro || steps.length === 0 && authFlow.type === 'OAuth' ? -1 /* intro screen */ : 0 /* first step */;
        });
        setAuthError(undefined);
      }
    });
  }
  if (authState.status === 'succeeded' && successScreen) {
    return successScreen({
      appName,
      authResponse: authState.authResponse,
      onCancel
    });
  }
  return /*#__PURE__*/_jsx(ModalOrInlineLayout, {
    isModal: isModal,
    header: isModal ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: isReauth ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.modal.reconnectDialogHeader",
          options: {
            appName
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.modal.dialogHeader",
          options: {
            appName
          }
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      })]
    }) : undefined,
    body: /*#__PURE__*/_jsxs(Section, {
      paddingBlockEnd: "100",
      children: [inline ? /*#__PURE__*/_jsx(View, {
        children: steps.map((step, ix) => /*#__PURE__*/_jsxs(Fragment, {
          children: [ix > 0 ? /*#__PURE__*/_jsx(HR, {
            distance: "large"
          }) : null, /*#__PURE__*/_jsxs(View, {
            marginBlockEnd: ix === steps.length - 1 ? '1000' : undefined,
            children: [step.title && /*#__PURE__*/_jsx(Text, {
              as: "h5",
              marginBlockEnd: "200",
              children: step.title
            }), /*#__PURE__*/_jsx(View, {
              marginInlineStart: "400",
              children: /*#__PURE__*/_jsx(View, {
                paddingInlineStart: "50",
                children: step.items.map(stepItem => /*#__PURE__*/_jsx(AuthStepItemView, {
                  item: stepItem.item,
                  label: stepItem.label,
                  params: params,
                  onChange: (name, value) => {
                    setParams(prevParams => Object.assign({}, prevParams, {
                      [name]: value
                    }));
                  }
                }, stepItem.label))
              })
            })]
          })]
        }, ix))
      }) : null, currentStep ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [!isReauth ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: appIcon ? /*#__PURE__*/_jsx(Image, {
              src: appIcon,
              blockSize: "40px",
              inlineSize: "40px",
              objectFit: "contain"
            }) : /*#__PURE__*/_jsx(Illustration, {
              name: "integrations",
              blockSize: "40px",
              inlineSize: "40px"
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.auth.connectIntro",
              options: {
                appName
              }
            })
          }), /*#__PURE__*/_jsx(HR, {})]
        }) : null, authError && /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "600",
          as: "div",
          children: /*#__PURE__*/_jsx(AuthErrorAlert, {
            appName: appName,
            error: authError
          })
        }), currentStep.items.map(stepItem => /*#__PURE__*/_jsx(AuthStepItemView, {
          item: stepItem.item,
          label: stepItem.label,
          params: params,
          onChange: (name, value) => {
            setParams(prevParams => Object.assign({}, prevParams, {
              [name]: value
            }));
          }
        }, stepItem.label))]
      }) : stepIndex === -1 ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [authError && /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "600",
          as: "div",
          children: /*#__PURE__*/_jsx(AuthErrorAlert, {
            appName: appName,
            error: authError
          })
        }), /*#__PURE__*/_jsx(IntroStep, {
          type: authFlow.type,
          appName: appName,
          introScreen: introScreen
        })]
      }) : null]
    }),
    footer: /*#__PURE__*/_jsx(ButtonWrapper, {
      paddingInlineStart: inline ? '400' : '0',
      paddingBlockStart: inline ? '300' : '0',
      children: /*#__PURE__*/_jsxs(View, {
        paddingInlineStart: inline ? '50' : '0',
        children: [!inline && stepIndex > firstScreenIndex &&
        /*#__PURE__*/
        // Back button
        _jsx(Button, {
          use: "secondary",
          onClick: () => setStepIndex(ix => ix - 1),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.auth.back"
          })
        }), !inline && stepIndex < steps.length - 1 ?
        /*#__PURE__*/
        // Next button
        _jsx(Button, {
          use: "primary",
          onClick: () => setStepIndex(ix => ix + 1),
          disabled: !isValidStep,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.auth.next"
          })
        }) : /*#__PURE__*/_jsx(FinishButton, {
          sessionId: authFlow.sessionId,
          type: authFlow.type,
          portalId: portalId,
          appName: appName,
          disabled: inline ? !allStepsValid : !isValidStep,
          steps: steps,
          params: params,
          oAuthScope: oAuthScope,
          mcp: mcp,
          appId: appId,
          onSuccess: response => {
            setAuthError(undefined); // clear any previous errors
            onComplete(response);
          },
          onFailure: err => setAuthError(err),
          getAuthorizeUrl: getAuthorizeUrl,
          finishAuthFlow: finishAuthFlow
        })]
      })
    })
  });
}
export function UnifiedAuthView({
  isModal,
  isReauth = false,
  portalId,
  onComplete,
  onCancel = () => {},
  fetchAuthConfig,
  introScreen,
  successScreen
}) {
  const [authState, setAuthState] = useState({
    status: 'pending'
  });
  const [configFetchState, setConfigFetchState] = useState({
    status: 'pending'
  });
  useEffect(() => {
    let cancelled = false;
    fetchAuthConfig().then(authConfig => {
      if (!cancelled) {
        setConfigFetchState(() => ({
          status: 'succeeded',
          config: authConfig
        }));
      }
    }).catch(err => {
      if (!cancelled) {
        setConfigFetchState(() => ({
          status: 'failed',
          err
        }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const children = /*#__PURE__*/_jsx(_Fragment, {
    children: configFetchState.status === 'pending' ? /*#__PURE__*/_jsx(AuthConfigLoadingState, {
      isModal: isModal,
      onCancel: onCancel
    }) : configFetchState.status === 'succeeded' ? /*#__PURE__*/_jsx(LowLevelAuthFlowView, {
      isModal: isModal,
      isReauth: isReauth,
      portalId: portalId,
      authConfig: configFetchState.config,
      onComplete: payload => {
        setAuthState({
          status: 'succeeded',
          authResponse: payload
        });
        if (onComplete) onComplete(payload);
      },
      onCancel: onCancel,
      introScreen: introScreen,
      successScreen: successScreen,
      authState: authState
    }) : /*#__PURE__*/_jsx(AuthConfigFetchFailure, {
      isModal: isModal,
      onClose: onCancel
    })
  });
  return isModal ? /*#__PURE__*/_jsx(Modal, {
    "data-test-id": "unified-auth-modal",
    width: 550
    // This is necessary to layout the success UIDialogHeaderImage correctly
    ,
    use: authState.status === 'succeeded' ? 'success' : 'default',
    children: children
  }) : /*#__PURE__*/_jsx(View, {
    "data-test-id": "unified-auth-form",
    children: children
  });
}